/**
 * Canonical category URL helpers for shop routes (`/shop/c/...`).
 * Slugs use Dutch labels by default so URLs stay stable across UI languages.
 */

const {
  categoryLabel,
  findCategoryById,
  getParentChain,
} = require("./categoryBreadcrumb");

/** Language used for URL slug segments (canonical). */
const CATEGORY_URL_LANG = "nl";

const slugifyCategoryLabel = (value) => {
  if (value == null) {
    return "";
  }
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
};

const getAllCategoryIds = (item) => {
  if (!item?.id) {
    return [];
  }
  let ids = [item.id];
  if (item.children && item.children.length > 0) {
    item.children.forEach((child) => {
      ids = [...ids, ...getAllCategoryIds(child)];
    });
  }
  return ids;
};

const categoryIdsEqual = (a, b) => {
  const left = Array.isArray(a) ? a.map(String) : a ? [String(a)] : [];
  const right = Array.isArray(b) ? b.map(String) : b ? [String(b)] : [];
  if (left.length !== right.length) {
    return false;
  }
  return left.every((id, index) => id === right[index]);
};

const siblingSlugBase = (category, language) =>
  slugifyCategoryLabel(categoryLabel(category, language));

const categoryPathSegment = (category, siblings, language = CATEGORY_URL_LANG) => {
  const base = siblingSlugBase(category, language);
  if (!base) {
    return String(category.id);
  }
  const collisions = (siblings || []).filter(
    (sibling) => siblingSlugBase(sibling, language) === base
  );
  if (collisions.length > 1) {
    return `${base}--${category.id}`;
  }
  return base;
};

const buildCategoryPathSegments = (
  chain,
  rootCategories,
  language = CATEGORY_URL_LANG
) => {
  if (!Array.isArray(chain) || chain.length === 0) {
    return [];
  }

  return chain.map((category, index) => {
    const siblings =
      index === 0 ? rootCategories || [] : chain[index - 1]?.children || [];
    return categoryPathSegment(category, siblings, language);
  });
};

const parseCategoryPathSplat = (splat) => {
  if (!splat || typeof splat !== "string") {
    return [];
  }
  return splat
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
};

const resolveSegmentAmongSiblings = (
  siblings,
  segment,
  language = CATEGORY_URL_LANG
) => {
  if (!Array.isArray(siblings) || !segment) {
    return null;
  }

  const idSuffixMatch = String(segment).match(/^(.*)--(.+)$/);
  if (idSuffixMatch) {
    const byId = siblings.find(
      (sibling) => String(sibling.id) === String(idSuffixMatch[2])
    );
    if (byId) {
      return byId;
    }
  }

  const matches = siblings.filter((sibling) => {
    const base = siblingSlugBase(sibling, language);
    return (
      base === segment ||
      `${base}--${sibling.id}` === segment ||
      String(sibling.id) === segment
    );
  });

  return matches[0] || null;
};

/**
 * Walk the category tree using URL slug segments.
 * @returns {object|null} The matched leaf category node
 */
const resolveCategoryFromPathSegments = (
  categories,
  segments,
  language = CATEGORY_URL_LANG
) => {
  if (!Array.isArray(categories) || !Array.isArray(segments) || segments.length === 0) {
    return null;
  }

  let siblings = categories;
  let current = null;

  for (const segment of segments) {
    current = resolveSegmentAmongSiblings(siblings, segment, language);
    if (!current) {
      return null;
    }
    siblings = current.children || [];
  }

  return current;
};

const buildCategoryRelativePath = (
  categoryOrId,
  categories,
  language = CATEGORY_URL_LANG
) => {
  const category =
    typeof categoryOrId === "object" && categoryOrId?.id != null
      ? categoryOrId
      : findCategoryById(categories, categoryOrId);

  if (!category) {
    return null;
  }

  const chain = getParentChain(categories, category.id);
  if (!chain.length) {
    return null;
  }

  const segments = buildCategoryPathSegments(chain, categories, language);
  if (!segments.length) {
    return null;
  }

  return `c/${segments.join("/")}`;
};

/**
 * @param {string} shopBaseUrl e.g. `/site/10/shop` or `https://www.awtech.be/shop`
 * @returns {string|null}
 */
const buildCategoryShopPath = (
  shopBaseUrl,
  categoryOrId,
  categories,
  language = CATEGORY_URL_LANG
) => {
  const relative = buildCategoryRelativePath(
    categoryOrId,
    categories,
    language
  );
  if (!relative || !shopBaseUrl) {
    return null;
  }
  return `${String(shopBaseUrl).replace(/\/$/, "")}/${relative}`;
};

/**
 * Flatten every category node to a sitemap-friendly path entry.
 * @returns {Array<{ id: string, path: string, segments: string[], depth: number }>}
 */
const collectAllCategoryPaths = (categories, language = CATEGORY_URL_LANG) => {
  const results = [];

  const walk = (nodes, parentChain) => {
    if (!Array.isArray(nodes)) {
      return;
    }
    for (const node of nodes) {
      const chain = [...parentChain, node];
      const segments = buildCategoryPathSegments(chain, categories, language);
      if (segments.length) {
        results.push({
          id: node.id,
          path: `c/${segments.join("/")}`,
          segments,
          depth: chain.length,
        });
      }
      if (node.children?.length) {
        walk(node.children, chain);
      }
    }
  };

  walk(categories, []);
  return results;
};

module.exports = {
  CATEGORY_URL_LANG,
  buildCategoryPathSegments,
  buildCategoryRelativePath,
  buildCategoryShopPath,
  categoryIdsEqual,
  categoryPathSegment,
  collectAllCategoryPaths,
  getAllCategoryIds,
  parseCategoryPathSplat,
  resolveCategoryFromPathSegments,
  slugifyCategoryLabel,
};
